/**
 * The numbers VOSviewer draws with, all in one place.
 *
 * Every constant here is a field of `com.vosviewer.ItemVisualization`,
 * `ItemLinkVisualization` or `DensityVisualization`, with the Java name in the
 * comment.  They are exported so an application can surface them as defaults in
 * a settings panel without re-deriving them.
 */

/** `ItemVisualization.LABEL_MAIN_TEXT_MIN_FONT_SIZE` — the font size at weight 0. */
export const LABEL_MIN_FONT_SIZE = 9;

/** `ItemVisualization.LABEL_MAIN_TEXT_FONT_SIZE_SCALING_CONSTANT` — added at weight = mean. */
export const LABEL_FONT_SIZE_SCALING = 4;

/** `ItemVisualization.LABEL_SUB_TEXT_FONT_SIZE`. */
export const SUBLABEL_FONT_SIZE = 8;

/** `ItemVisualization.LABEL_SUB_TEXT_MIN_LENGTH` — a sublabel is never truncated below this. */
export const SUBLABEL_MIN_LENGTH = 15;

/** `ItemVisualization.LABEL_MAIN_SUB_TEXT_REL_LENGTH` — a sublabel may be 1.3x the label's width. */
export const SUBLABEL_MAX_REL_WIDTH = 1.3;

/** `ItemVisualization.LABEL_MAIN_TEXT_MAX_LENGTH`. */
export const LABEL_MAX_LENGTH = 30;

/** `ItemVisualization.LABEL_SCALING_CONSTANT` — labels start fading in at 0.6x their threshold. */
export const LABEL_FADE_START = 0.6;

/** `ItemVisualization.LABEL_MIN_ALPHA` / `LABEL_MAX_ALPHA`, as 0..1 fractions. */
export const LABEL_MIN_ALPHA = 10 / 255;
export const LABEL_MAX_ALPHA = 230 / 255;

/** `ItemVisualization.CIRCLE_AVERAGE_DIAMETER` — the diameter of an item of mean weight. */
export const CIRCLE_AVERAGE_DIAMETER = 16;

/** `ItemVisualization.CIRCLE_MIN_DIAMETER`. */
export const CIRCLE_MIN_DIAMETER = 5;

/** `ItemVisualization.CIRCLE_EXTRA_DIAMETER_SELECTED`. */
export const CIRCLE_EXTRA_DIAMETER_SELECTED = 2;

/** `ItemVisualization.LABEL_SIZE_VARIATION` — the exponent v in w^v. */
export const LABEL_SIZE_VARIATION = 0.5;

/** `ItemVisualization.CIRCLE_ALPHA`, as a 0..1 fraction. */
export const CIRCLE_ALPHA = 180 / 255;

/** `ItemColors` Lab transition strengths for the circle's vertical gradient. */
export const CIRCLE_LIGHT_TRANSITION = 0.45;
export const CIRCLE_DARK_TRANSITION = 0.15;

/** `ItemColors` — a link takes its endpoints' colour lightened by this much. */
export const LINE_LIGHT_TRANSITION = 0.4;

/** `ItemLinkVisualization.LINE_ALPHA` / `LINE_ALPHA_HIGHLIGHTED`, as 0..1 fractions. */
export const LINE_ALPHA = 110 / 255;
export const LINE_ALPHA_HIGHLIGHTED = 200 / 255;

/** `ItemLinkVisualization.LINE_MIN_WIDTH` / `LINE_AVERAGE_WIDTH` / `LINE_SIZE_VARIATION`. */
export const LINE_MIN_WIDTH = 0.1;
export const LINE_AVERAGE_WIDTH = 1.5;
export const LINE_SIZE_VARIATION = 0.5;

/** `ItemLinkVisualization.MAX_N_LINES` — VOSviewer's default cap on drawn lines. */
export const MAX_N_LINES = 1000;

/** `MapData` averages the top this-many link strengths to get the rescaling denominator. */
export const LINE_STRENGTH_MEAN_TOP_N = 1000;

/** `DensityVisualization.KERNEL_WIDTH` — a fraction of the mean pairwise distance. */
export const KERNEL_WIDTH = 0.12;

/** `DensityVisualization.COLOR_TRANSFORMATION` — the exponent g in the strength curve. */
export const COLOR_TRANSFORMATION = 0.55;

/** `DensityVisualization.GRID_SIZE`. */
export const DENSITY_GRID_SIZE = 250;

/** `DensityVisualization.ITEM_DENSITY_THRESHOLD` — below this an item stops contributing. */
export const ITEM_DENSITY_THRESHOLD = 1e-4;

/** `DensityVisualization.MIN_RELATIVE_CLUSTER_WEIGHT`. */
export const MIN_RELATIVE_CLUSTER_WEIGHT = 0.02;

/** `OverlayColors.PROP_SCORES_BETWEEN_MIN_AND_MAX` — 0.8 gives the 10th and 90th percentiles. */
export const OVERLAY_SCORE_PROPORTION = 0.8;

/** Desired tick count for the overlay colour bar, `Ticks(min, max, 5)`. */
export const OVERLAY_DESIRED_TICKS = 5;

/** The density grid's world extent is the item bounding box padded by this fraction. */
export const DENSITY_EXTENT_PADDING = 0.1;

/** Default label font stack. VOSviewer uses Open Sans; this degrades sanely. */
export const DEFAULT_FONT_FAMILY =
  '"Open Sans", "Segoe UI", -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif';
